import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 60. EXTENSIBILITÉ
// Un futur module doit pouvoir ENREGISTRER, sans modifier profondément le Core :
//   nouvelles entités · nouvelles permissions · nouveaux événements ·
//   nouveaux outils AI · nouvelles pages · nouveaux workflows · nouveaux paramètres.
//
// Le core_extension_registry stocke ces déclarations de manière déclarative.
// Le Core les expose ensuite aux autres briques (permissions, événements,
// Tool Gateway…) par simple lecture.
// ─────────────────────────────────────────────────────────────

export type ExtensionKind = 'entity' | 'permission' | 'event' | 'ai_tool' | 'page' | 'workflow' | 'setting';

export const EXTENSION_KINDS: { key: ExtensionKind; label: string; icon: string; className: string; desc: string }[] = [
  { key: 'entity', label: 'Entité', icon: 'ri-database-2-line', className: 'bg-primary-100 text-primary-900', desc: 'Nouvelle table / ressource métier.' },
  { key: 'permission', label: 'Permission', icon: 'ri-shield-keyhole-line', className: 'bg-secondary-100 text-secondary-900', desc: 'Nouvelle permission granulaire.' },
  { key: 'event', label: 'Événement', icon: 'ri-flashlight-line', className: 'bg-accent-100 text-accent-900', desc: 'Nouvel événement de l’Event Bus.' },
  { key: 'ai_tool', label: 'Outil AI', icon: 'ri-robot-2-line', className: 'bg-accent-100 text-accent-900', desc: 'Nouvel outil exposé au Zifek AI.' },
  { key: 'page', label: 'Page', icon: 'ri-layout-2-line', className: 'bg-background-100 text-foreground-700', desc: 'Nouvelle page du module.' },
  { key: 'workflow', label: 'Workflow', icon: 'ri-flow-chart', className: 'bg-secondary-100 text-secondary-900', desc: 'Nouveau workflow automatique.' },
  { key: 'setting', label: 'Paramètre', icon: 'ri-settings-3-line', className: 'bg-background-100 text-foreground-700', desc: 'Nouveau paramètre configurable.' },
];

export function extensionKindMeta(key: string | null | undefined) {
  return EXTENSION_KINDS.find((k) => k.key === key) || EXTENSION_KINDS[0];
}

export interface CoreExtension {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  module_key: string | null;
  kind: string;
  key: string;
  label: string | null;
  payload: Record<string, unknown> | null;
  created_at: string | null;
}

export async function listExtensions(organizationId: number, filter?: { kind?: string; moduleKey?: string }): Promise<CoreExtension[]> {
  let query = supabase
    .from('core_extension_registry')
    .select('*')
    .or(`organization_id.is.null,organization_id.eq.${organizationId}`)
    .order('created_at', { ascending: false });
  if (filter?.kind) query = query.eq('kind', filter.kind);
  if (filter?.moduleKey) query = query.eq('module_key', filter.moduleKey);
  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as CoreExtension[];
}

export interface RegisterExtensionInput {
  tenantId?: number | null;
  organizationId: number;
  moduleKey: string;
  kind: ExtensionKind;
  key: string;
  label?: string | null;
  payload?: Record<string, unknown> | null;
}

/** Enregistre une extension déclarative (idempotent par clé + module + type). */
export async function registerExtension(input: RegisterExtensionInput): Promise<number | null> {
  const existing = await supabase
    .from('core_extension_registry')
    .select('id')
    .eq('organization_id', input.organizationId)
    .eq('module_key', input.moduleKey)
    .eq('kind', input.kind)
    .eq('key', input.key)
    .maybeSingle();
  if (existing.data?.id) return existing.data.id as number;

  const { data, error } = await supabase
    .from('core_extension_registry')
    .insert({
      tenant_id: input.tenantId ?? null,
      organization_id: input.organizationId,
      module_key: input.moduleKey,
      kind: input.kind,
      key: input.key,
      label: input.label || null,
      payload: input.payload ?? null,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function deleteExtension(id: number): Promise<void> {
  const { error } = await supabase.from('core_extension_registry').delete().eq('id', id);
  if (error) throw error;
}

export interface ExtensibilitySummary {
  total: number;
  byKind: Record<string, number>;
  byModule: string[];
}

export async function fetchExtensibilitySummary(organizationId: number): Promise<ExtensibilitySummary> {
  const rows = await listExtensions(organizationId);
  const byKind: Record<string, number> = {};
  const modules = new Set<string>();
  rows.forEach((r) => {
    byKind[r.kind] = (byKind[r.kind] || 0) + 1;
    if (r.module_key) modules.add(r.module_key);
  });
  return { total: rows.length, byKind, byModule: Array.from(modules) };
}

/** Extensions prêtes à l'emploi : nouveau module « Loyalty » (démonstration). */
export const SAMPLE_MODULE_EXTENSIONS: Omit<RegisterExtensionInput, 'organizationId' | 'tenantId'>[] = [
  { moduleKey: 'loyalty', kind: 'entity', key: 'loyalty_card', label: 'Carte de fidélité' },
  { moduleKey: 'loyalty', kind: 'permission', key: 'loyalty.read', label: 'Consulter la fidélité' },
  { moduleKey: 'loyalty', kind: 'permission', key: 'loyalty.write', label: 'Gérer la fidélité' },
  { moduleKey: 'loyalty', kind: 'event', key: 'points.earned', label: 'Points gagnés' },
  { moduleKey: 'loyalty', kind: 'ai_tool', key: 'get_loyalty_balance', label: 'Solde de fidélité' },
  { moduleKey: 'loyalty', kind: 'page', key: '/dashboard/apps/ma-fidelite', label: 'Espace fidélité' },
  { moduleKey: 'loyalty', kind: 'workflow', key: 'welcome_bonus', label: 'Bonus de bienvenue' },
  { moduleKey: 'loyalty', kind: 'setting', key: 'points_per_currency', label: 'Points par unité monétaire' },
];